import { PropertyComparison } from 'types/dynamic-forms/DocumentDiff';

export type CompareSide = 'left' | 'right';

/**
 * Colour of a single cell in a given column:
 * - `reference` — the reference (left) column's changed/removed value (red tone).
 * - `compared`  — the compared (right) column's changed/added value (green tone).
 * - `empty`     — this side has no value because the field was added/removed (grey placeholder).
 * - `undefined` — unchanged (white).
 */
export type CellVariant = 'reference' | 'compared' | 'empty';

/**
 * How much of a cell is drawn:
 * - `placeholder` — grey box only: the field does not exist on this side of the comparison.
 * - `labelOnly`   — the field exists on this side but holds no value.
 * - `value`       — label and value.
 */
export type CellPresentation = 'placeholder' | 'labelOnly' | 'value';

export interface CellDisplay {
  variant?: CellVariant;
  presentation: CellPresentation;
}

// The backend records every path it visits, but not children of an array row present on one side only,
// nor a metadata row naming a field the backend no longer exposes: both arrive here as undefined.
const readSide = (comparison: PropertyComparison | undefined, side: CompareSide) => ({
  state: comparison?.state,
  isValueEmpty: comparison ? (side === 'left' ? comparison.isEmptyA : comparison.isEmptyB) : false,
  isDefinedInSchema:
    comparison === undefined ||
    (side === 'left' ? comparison.isDefinedInSchemaA : comparison.isDefinedInSchemaB),
});

/**
 * The single decision for how one comparison cell is drawn. Block renderers (files, images) hide their own
 * content when there is nothing to show, so they use the colour and ignore the presentation.
 */
export const getCellPresentation = (comparison: PropertyComparison | undefined, side: CompareSide): CellDisplay => {
  const { state, isValueEmpty, isDefinedInSchema } = readSide(comparison, side);

  if (!isDefinedInSchema) {
    return { variant: 'empty', presentation: 'placeholder' };
  }
  // An empty value in the compared version keeps its label, so the reader sees the field went blank. The
  // backend derives the state from emptiness, so this also covers every RemovedFromB on the right.
  if (side === 'right' && isValueEmpty) {
    return { variant: 'empty', presentation: 'labelOnly' };
  }
  if (!state || state === 'Equal') {
    return { presentation: 'value' };
  }
  // The reference version drops the label instead, so an addition reads as "did not exist yet".
  if (side === 'left' && state === 'AddedInB') {
    return { variant: 'empty', presentation: 'placeholder' };
  }
  return { variant: side === 'left' ? 'reference' : 'compared', presentation: 'value' };
};
